import React, { useEffect, useMemo, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import RewardWheel from "@/components/public/RewardWheel";
import ScratchCard from "@/components/public/ScratchCard";
import LeadFormModal from "@/components/public/LeadFormModal";
import PrizeRevealModal from "@/components/public/PrizeRevealModal";
import ThemeWrap, { themeConfettiColors } from "@/components/public/ThemeWrap";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Loader2, Gift, Trophy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { playWinSound } from "@/lib/spinSound";
import { motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { linkifyText } from "@/lib/linkify";
import { composeTheme, slugifyBusinessType } from "@/lib/categoryThemes";
import { normalizePalette } from "@/lib/safeColors";

export default function PlayPage() {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  // Bumped every time the "Spin the Wheel!" button is tapped — passed as
  // React `key` on <LeadFormModal> so the form fully remounts with a
  // fresh state each time (no leftover typed values, no lingering errors,
  // no cached OTP step).
  const [formAttempt, setFormAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  // Pending lead form data — held between form-submit and OTP-verify when
  // the campaign requires email OTP. When OTP isn't required, this stays
  // null and the form data flows straight to api.spin(). The OTP step
  // now lives inside the same LeadFormModal (single popup) so a bad OTP
  // keeps the typed name/phone/email available on retry.
  const [pendingLead, setPendingLead] = useState(null);
  const [otpStep, setOtpStep] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(60);

  const [spinningTo, setSpinningTo] = useState(null);
  const [spinResult, setSpinResult] = useState(null);
  const [revealOpen, setRevealOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .publicCampaign(campaignId)
      .then((r) => alive && setCampaign(r.data))
      .catch((e) => {
        if (!alive) return;
        // Backend error branches sometimes return {message, code, ...}
        // (e.g. 402 pending_payment / 402 trial_ended / 410 sold_out).
        // Coerce to a plain string so React never tries to render an
        // object as a child — this was crashing the whole page.
        const det = e?.response?.data?.detail;
        const msg =
          typeof det === "string"
            ? det
            : det && typeof det === "object"
            ? det.message || det.code || "Campaign not available"
            : "Campaign not available";
        setError(String(msg));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [campaignId]);

  const handleSpinClick = () => {
    setServerError("");
    setPendingLead(null);
    setOtpStep(false);
    // Bump the attempt counter so the modal mounts fresh — see
    // formAttempt state above.
    setFormAttempt((n) => n + 1);
    setFormOpen(true);
  };

  // Run the actual /spin request once we have a verified lead.
  const _doSpin = async (data) => {
    setSubmitting(true);
    setServerError("");
    try {
      const r = await api.spin(campaignId, data);
      setFormOpen(false);
      setOtpStep(false);
      setPendingLead(null);
      // small delay so drawer closes
      setTimeout(() => {
        setSpinResult(r.data);
        setSpinningTo(r.data.winning_index);
      }, 220);
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "string" ? det : det?.message || "Spin failed. Please try again.";
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submitForm = async (data) => {
    setServerError("");
    // OTP flow: campaign requires email verification first.
    if (campaign?.require_email_otp) {
      const email = (data?.email || "").trim();
      if (!email) {
        setServerError("Email is required for this campaign.");
        return;
      }
      setSubmitting(true);
      try {
        const r = await api.sendLeadOtp(campaignId, { email, name: data?.name });
        setOtpCooldown(Number(r?.data?.cooldown_seconds) || 60);
        setPendingLead(data);
        setOtpStep(true);
        // Positive confirmation — the user JUST typed their email; a
        // silent "next screen" swap left them guessing whether the
        // code was actually sent. Explicit toast fixes that.
        toast.success(`We've emailed a 6-digit code to ${email}.`);
      } catch (e) {
        const det = e?.response?.data?.detail;
        if (det?.retry_after) setOtpCooldown(Number(det.retry_after));
        const msg = typeof det === "string" ? det : det?.message || "Couldn't send code. Please try again.";
        setServerError(msg);
        // Show the server's actual message (rate-limit, invalid email, etc.)
        // in a toast — the inline serverError still displays in the
        // modal but a toast makes the failure obvious at a glance.
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // No OTP — spin straight away.
    await _doSpin(data);
  };

  const handleOtpVerify = async (code) => {
    if (!pendingLead) return;
    setSubmitting(true);
    setServerError("");
    try {
      await api.verifyLeadOtp(campaignId, { email: pendingLead.email, code });
      // OTP verified — now actually spin (this closes the modal on success).
      await _doSpin(pendingLead);
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "string" ? det : det?.message || "Invalid or expired code. Please try again.";
      setServerError(msg);
      // Toast the specific server error (wrong code / expired / retries
      // exhausted) so the user acts on it rather than silently retyping.
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpResend = async () => {
    if (!pendingLead) return {};
    try {
      const r = await api.sendLeadOtp(campaignId, {
        email: pendingLead.email,
        name: pendingLead.name,
      });
      toast.success("A fresh code is on its way.");
      return r?.data || {};
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "string" ? det : det?.message || "Couldn't resend the code. Please try again.";
      toast.error(msg);
      // Return the cooldown from the error payload so the modal can
      // still disable the resend button for the throttled window.
      return det && typeof det === "object" ? det : {};
    }
  };

  const onSpinEnd = () => {
    // Fire the campaign's configured win-sound the moment the wheel
    // lands. Silent when spin_sound="none" or the browser lacks
    // Web Audio support. Never throws.
    if (!spinResult?.winning_slice?.is_better_luck) {
      playWinSound(campaign?.spin_sound);
    }
    setRevealOpen(true);
  };

  const closeReveal = (open) => {
    setRevealOpen(open);
    if (!open) {
      // reset to allow re-render but keep result text visible
    }
  };

  const confettiColors = useMemo(
    () => themeConfettiColors(campaign?.theme || "christmas"),
    [campaign?.theme]
  );

  /* ── PHASE 2 theme resolution ─────────────────────────────────────
     Three inputs, resolved in priority order:
       1. campaign.category_theme  — vendor set it explicitly in wizard
       2. slugify(vendor.business_type) — auto-detect from vendor profile
       3. "other" — CityDooh gold + black fallback
     Then SafeColors normalizes the vendor's palette against the
     category surface so slice labels never render illegible. */
  const activeTheme = useMemo(() => {
    if (!campaign) return null;
    const cat = campaign.category_theme
      || slugifyBusinessType(campaign.vendor_business_type || campaign.business_type);
    const palette = normalizePalette({
      primary: campaign.primary_color,
      secondary: campaign.secondary_color,
      theme: { slicePrimary: "#F5A800", sliceSecondary: "#0A0A0A" },
    });
    return composeTheme({
      categorySlug: cat,
      intensity: campaign.theme_intensity || "branded",
      manualVariant: campaign.manual_theme_variant,
      safePalette: palette,
    });
  }, [campaign]);

  if (loading) {
    return (
      <ThemeWrap theme="christmas">
        <div className="min-h-screen grid place-items-center text-foreground">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading your game...</span>
          </div>
        </div>
      </ThemeWrap>
    );
  }

  if (error || !campaign) {
    return (
      <ThemeWrap theme="christmas">
        <div className="min-h-screen grid place-items-center text-foreground px-6">
          <div className="max-w-md text-center" data-testid="page-error-state">
            <Sparkles className="mx-auto h-10 w-10 opacity-70" />
            <h1 className="font-heading text-3xl mt-3">Campaign unavailable</h1>
            <p className="mt-2 opacity-80">{error || "This campaign is no longer active."}</p>
          </div>
        </div>
      </ThemeWrap>
    );
  }

  // Defensive routing — legacy QR codes / bookmarks / cached share
  // links for static_offer campaigns still point at /play/{id}. Rather
  // than render a broken wheel with only 1 slice, hard-redirect to the
  // dedicated /offers/{id} instant-claim page. build_campaign_url has
  // been fixed going forward; this guard heals the historical links.
  if ((campaign.game_type || "").toLowerCase() === "static_offer") {
    return <Navigate to={`/offers/${campaignId}`} replace />;
  }

  // SOLD OUT — all stock has been claimed. Stop spinning and show a clear message.
  if (campaign.stock && campaign.stock.sold_out) {
    return (
      <ThemeWrap theme={campaign.theme} customBg={campaign.theme === "custom" ? assetUrl(campaign.custom_theme_bg) : undefined}>
        <div className="min-h-screen grid place-items-center text-foreground px-6">
          <div className="max-w-md text-center bg-white/10 border border-white/20 backdrop-blur rounded-3xl p-8 shadow-2xl" data-testid="play-sold-out-state">
            <div className="h-16 w-16 mx-auto rounded-full bg-amber-400/20 grid place-items-center mb-3">
              <Sparkles className="h-8 w-8 text-amber-300" />
            </div>
            <h1 className="font-heading text-3xl font-semibold">Campaign sold out!</h1>
            <p className="mt-2 opacity-80 text-sm">
              Every prize from this campaign has already been claimed. Thank you to everyone
              who participated — keep an eye out for our next promotion.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest opacity-70">
              <span>Powered by</span>
              <BrandMark onDark size="game" aria-label="City DOOH" />
            </div>
          </div>
        </div>
      </ThemeWrap>
    );
  }

  return (
    <ThemeWrap theme={campaign.theme} customBg={campaign.theme === "custom" ? assetUrl(campaign.custom_theme_bg) : undefined}>
      <Toaster richColors position="top-center" />

      {/* ── PHASE 2: Category theme backdrop ─────────────────────────
          Composed from:
            • campaign.category_theme (vendor-picked in the wizard)
            • fallback: slugified vendor.business_type
            • fallback: "other" (CityDooh black + gold)
          Vendor primary/secondary_color go through SafeColors so we
          never render illegible slice labels. Intensity dials the
          decoration up/down (default "branded"). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{ background: activeTheme.backgroundBase }}
        data-testid="play-category-backdrop"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: activeTheme.textureImage,
          backgroundRepeat: "repeat",
          opacity: activeTheme.textureOpacity,
          mixBlendMode: "screen",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background: `radial-gradient(55% 45% at 50% 38%, ${activeTheme.ambientColor} 0%, rgba(0,0,0,0) 65%)`,
          opacity: activeTheme.ambientOpacity,
        }}
      />

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-8 pb-16 text-foreground">
        {/* ── CAMPAIGN BRAND HEADER ───────────────────────────────────
            Vendor identity is prominent but the wheel remains the star.
            Structure per redesign brief:
              [Vendor Logo]
              [Vendor business / Campaign name]
              SPIN & WIN
              [supporting line]                              */}
        <motion.header
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
          data-testid="play-brand-header"
        >
          {campaign.logo_url ? (
            <div className="mx-auto mb-3 h-16 w-16 rounded-2xl overflow-hidden bg-white ring-2 ring-[#F5A800]/70 shadow-[6px_6px_0_rgba(245,168,0,0.35)]">
              <img
                src={assetUrl(campaign.logo_url)}
                alt={`${campaign.vendor_business || campaign.name} logo`}
                // A7 — object-cover matches OfferPage vendor tile
                // (`object-cover border-2`) and the SVG wheel hub
                // (`preserveAspectRatio="xMidYMid slice"`), so the logo
                // fills the container consistently across all three
                // game types instead of floating half-empty when the
                // source image is a wide format.
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div
              className="mx-auto mb-3 h-16 w-16 rounded-2xl bg-black grid place-items-center ring-2 ring-[#F5A800]/70 shadow-[6px_6px_0_rgba(245,168,0,0.35)]"
              aria-hidden="true"
            >
              <span
                className="text-[#F5A800]"
                style={{ fontFamily: '"Anton", sans-serif', fontSize: 28 }}
              >
                {String(campaign.vendor_business || campaign.name || "C")
                  .trim()
                  .charAt(0)
                  .toUpperCase() || "C"}
              </span>
            </div>
          )}

          {campaign.vendor_business && (
            <p
              className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/70"
              data-testid="play-vendor-business"
            >
              {campaign.vendor_business}
            </p>
          )}

          <h1
            className="mt-3 leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl uppercase"
            style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
            data-testid="play-campaign-title"
          >
            {(() => {
              // Three-way header per game_type — never a binary check
              // so a future fourth mode doesn't silently regress. Static
              // offer campaigns are meant to route to /offers/{id}
              // instead of this page; the third branch below exists as
              // a defensive fallback in case someone deep-links here.
              switch (campaign.game_type) {
                case "scratch_card":
                  return <>Scratch <span className="text-[#F5A800]">&amp;</span> Win</>;
                case "static_offer":
                  return <>Claim <span className="text-[#F5A800]">Your</span> Offer</>;
                default:
                  return <>Spin <span className="text-[#F5A800]">&amp;</span> Win</>;
              }
            })()}
          </h1>

          <p
            className="mt-3 text-base sm:text-lg font-semibold text-white/90"
            data-testid="play-campaign-name"
          >
            {campaign.name}
          </p>
          <p className="mt-1 text-xs sm:text-sm text-white/60">
            Exciting rewards for every customer • 1 {campaign.game_type === "scratch_card" ? "play" : "spin"} per phone
          </p>
        </motion.header>

        {/* ── GAME BODY ────────────────────────────────────────────
            Branches on campaign.game_type. Both components use the
            IDENTICAL parent contract:
              props.slices, primaryColor, secondaryColor, onSliceText,
              spinningTo (int once backend result is known), onSpinEnd.
            All shared UI above/below this block (header, banner,
            category theme background, CTA, disclaimer, T&C, bottom
            CTA) renders unchanged for both game types. */}
        <div className="mt-8 grid place-items-center">
          {campaign.game_type === "scratch_card" ? (
            <ScratchCard
              slices={campaign.slices}
              logoUrl={campaign.logo_url}
              primaryColor={activeTheme?.sliceA || campaign.primary_color || "#F5A800"}
              secondaryColor={activeTheme?.sliceB}
              onSliceText={activeTheme?.onSlice}
              spinningTo={spinningTo}
              onSpinEnd={onSpinEnd}
              onScratchThreshold={handleSpinClick}
              scratchSound={campaign.scratch_sound || "classic"}
              campaignName={campaign.name || ""}
              testId="scratch-card"
            />
          ) : (
            <RewardWheel
              slices={campaign.slices}
              logoUrl={campaign.logo_url}
              primaryColor={activeTheme?.sliceA || campaign.primary_color || "#F5A800"}
              secondaryColor={activeTheme?.sliceB}
              onSliceText={activeTheme?.onSlice}
              spinningTo={spinningTo}
              onSpinEnd={onSpinEnd}
              testId="wheel-canvas"
            />
          )}
        </div>

        {/* ── PRIMARY CTA — CityDooh gold pill, Anton display ─────── */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <Button
            size="lg"
            className="h-16 min-w-[240px] px-10 rounded-full text-lg font-bold uppercase tracking-wider transition-transform hover:-translate-y-0.5 focus-visible:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              backgroundColor: "#F5A800",
              color: "#0A0A0A",
              boxShadow: "6px 6px 0 #0A0A0A",
              fontFamily: '"Anton", "Impact", sans-serif',
              letterSpacing: "0.08em",
            }}
            onClick={handleSpinClick}
            disabled={spinningTo !== null && !revealOpen}
            data-testid="wheel-spin-button"
          >
            {spinningTo === null ? (
              <>
                <Gift className="mr-2 h-5 w-5" />
                {campaign.game_type === "scratch_card" ? "SCRATCH TO WIN" : "SPIN NOW"}
              </>
            ) : !revealOpen ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {campaign.game_type === "scratch_card" ? "REVEALING…" : "SPINNING…"}
              </>
            ) : (
              <>
                <Trophy className="mr-2 h-5 w-5" />
                VIEW PRIZE
              </>
            )}
          </Button>
          <p className="text-xs text-white/60">
            By {campaign.game_type === "scratch_card" ? "scratching" : "spinning"}, you agree to receive your coupon via SMS/Email.
          </p>
        </div>

        {/* ── FEATURED CAMPAIGN IMAGE (16:9) — vendor-authored banner ──
            Preserved between wheel/CTA and Terms & Conditions per the
            existing feature contract. Lazy-loaded so the wheel never
            waits on this decorative asset. */}
        {campaign.thumbnail_url && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="mt-10 rounded-2xl overflow-hidden ring-2 ring-white/10 shadow-[0_18px_40px_rgba(0,0,0,0.25)]"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="play-campaign-thumbnail"
          >
            <img
              src={assetUrl(campaign.thumbnail_url)}
              alt={`${campaign.name} banner`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </motion.div>
        )}

        {/* ── CAMPAIGN T&C — vendor-authored, whitespace-preserving.
            URLs are auto-linkified so vendor's "read more" link works.
            Existing feature contract preserved byte-for-byte. */}
        {campaign.terms_and_conditions && campaign.terms_and_conditions.trim() && (
          <div
            className="mt-10 rounded-2xl bg-black/40 border-2 border-white/10 backdrop-blur p-5"
            data-testid="play-campaign-terms"
          >
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-[#F5A800] mb-3">
              Campaign Terms &amp; Conditions
            </h3>
            <p className="text-xs sm:text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
              {linkifyText(campaign.terms_and_conditions, {
                className: "underline decoration-dotted underline-offset-2 hover:decoration-solid text-[#F5A800]",
              })}
            </p>
          </div>
        )}

        {/* Terms & Conditions — dynamic vendor-authored block, rendered
            above when `campaign.terms_and_conditions` is set. If the
            campaign somehow has no T&C on file (very old campaigns
            pre-terms feature), we intentionally show nothing here —
            the vendor is expected to author them via the campaign
            wizard, which auto-fills a boilerplate on new campaigns. */}

        {/* A1 — CityDooh platform disclaimer.
            Deliberately muted (neutral gray, not the vendor's campaign
            colors) because this is CityDooh's own liability copy, not
            the vendor's content. Sits between the vendor T&C card and
            the CityDooh CTA below. */}
        <div
          className="mt-6 text-center px-2"
          data-testid="play-platform-disclaimer"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">
            Know this before playing
          </p>
          <p className="mt-2 text-[11px] sm:text-xs text-white/50 leading-relaxed">
            This {campaign.game_type === "scratch_card" ? "scratch card" : "spin wheel"} and its offers are created and run by the business
            hosting this campaign. CityDooh provides the technology only and
            is not responsible for the offer, prize, or product. Please verify
            details and complete any purchase or payment directly with the
            business.
          </p>
        </div>
      </div>

      {/* A2 — CityDooh house CTA (site-branded, NOT vendor-themed).
          Rendered OUTSIDE the ThemeWrap's inner container so its colors
          don't inherit the vendor's campaign palette. Sits at the very
          bottom of the /play page and links to citydooh.com.

          Uses the exact same visual language as the CityDooh homepage
          hero: `.font-display` (Anton uppercase), `.mkt-highlight`
          (yellow paint-stroke behind the accent phrase), `.mkt-btn`
          (chunky black pill with yellow offset shadow). Wrapped in
          `.app-light` so those tokens resolve to the marketing palette
          instead of the vendor's dark theme. */}
      <section
        className="app-light relative mt-10 border-t-2 border-black text-[var(--mkt-ink)] overflow-hidden"
        style={{ background: "var(--mkt-paper)" }}
        data-testid="play-citydooh-cta"
      >
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20 text-center relative">
          <h2 className="font-display uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl">
            Want to build{" "}
            <span className="mkt-highlight">one for your business?</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-black/70 max-w-xl mx-auto font-medium">
            Turn every store visit into a qualified lead — just like this one.
          </p>
          <div className="mt-8 flex justify-center">
            <a
              href="https://citydooh.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mkt-btn"
              data-testid="play-citydooh-cta-button"
            >
              Get Started
              <span aria-hidden="true" className="ml-1">→</span>
            </a>
          </div>
        </div>
      </section>

      <LeadFormModal
        // React `key` bound to formAttempt forces a fresh mount every
        // time "Spin the Wheel!" is tapped — clears any leftover typed
        // values, focus state, or OTP-step-open state from a previous
        // cancelled attempt.
        key={`lead-attempt-${formAttempt}`}
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) {
            // User dismissed — clear the OTP step too so the next open
            // starts back on step 1.
            setOtpStep(false);
            setServerError("");
          }
        }}
        requireEmail={campaign.require_email || campaign.require_email_otp}
        customFields={campaign.custom_form_fields || []}
        primaryColor={campaign.primary_color}
        onSubmit={submitForm}
        submitting={submitting}
        serverError={serverError}
        otpRequired={campaign.require_email_otp}
        otpStep={otpStep}
        otpEmail={pendingLead?.email}
        otpCooldown={otpCooldown}
        onOtpVerify={handleOtpVerify}
        onOtpResend={handleOtpResend}
        gameType={campaign.game_type || "spin_wheel"}
      />

      <PrizeRevealModal
        open={revealOpen}
        onOpenChange={closeReveal}
        prize={spinResult?.winning_slice}
        couponCode={spinResult?.coupon_code}
        campaignName={campaign.name}
        vendorBusiness={campaign.vendor_business}
        campaignUrl={campaign.campaign_url}
        /* Per-spin redirect target — the spin API returns the effective
           URL for the winning slice (slice.redirect_url override →
           global post_spin_redirect_url → https://citydooh.com). We
           prefer this per-spin value so different slices on the same
           campaign can point to different destinations. Falls back to
           the campaign-level default while the modal is being opened
           from a prior state / before the spin response is set. */
        redirectUrl={spinResult?.redirect_url || campaign.post_spin_redirect_url}
        logoUrl={campaign.logo_url}
        thumbnailUrl={campaign.thumbnail_url}
        confettiColors={confettiColors}
        primaryColor={campaign.primary_color}
        gameType={campaign.game_type || "spin_wheel"}
      />
    </ThemeWrap>
  );
}
